import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'

export type MaintenanceType = 'ordinaria' | 'straordinaria'
export type MaintenanceStatus = 'attiva' | 'completata' | 'annullata'
export type MaintenanceScadenza = 'ok' | 'prossima' | 'scaduta'

export interface MaintenanceSchedule {
  id: number
  vehicle_id: number
  tipo: MaintenanceType
  descrizione: string
  km_scadenza: number | null
  data_scadenza: string | null
  km_preavviso: number
  giorni_preavviso: number
  stato: MaintenanceStatus
  ricorrente: boolean
  intervallo_km: number | null
  intervallo_giorni: number | null
  ultima_notifica: string | null
  data_completamento: string | null
  km_completamento: number | null
  note_completamento: string | null
  work_order_id: number | null
  created_at: string
  updated_at: string
  giorni_rimanenti: number | null
  stato_scadenza: MaintenanceScadenza
  vehicle_targa?: string
  vehicle_marca?: string
  vehicle_modello?: string
  vehicle_km_attuali?: number
  customer_id?: number
  customer_nome?: string
  customer_cognome?: string
  customer_telefono?: string
  customer_email?: string
}

export interface MaintenanceAlert {
  id: number
  vehicle_id: number
  vehicle_targa: string | null
  vehicle_marca: string | null
  vehicle_modello: string | null
  tipo: MaintenanceType
  descrizione: string
  data_scadenza: string | null
  km_scadenza: number | null
  days_until_due: number | null
  is_overdue: boolean
  alert_level: 'info' | 'warning' | 'critical'
}

export interface MaintenanceScheduleCreate {
  vehicle_id: number
  tipo: MaintenanceType
  descrizione: string
  km_scadenza?: number
  data_scadenza?: string
  km_preavviso?: number
  giorni_preavviso?: number
  stato?: MaintenanceStatus
  ricorrente?: boolean
  intervallo_km?: number
  intervallo_giorni?: number
}

export interface MaintenanceCompleteRequest {
  data_completamento?: string
  km_completamento?: number
  note_completamento?: string
}

export interface MaintenanceSendReminderRequest {
  tipo: 'email' | 'sms' | 'whatsapp'
  messaggio_personalizzato?: string
}

// ─── QUERIES ──────────────────────────────────────────────────────────────────

export const useMaintenanceSchedules = (filters?: {
  vehicle_id?: number
  status_filter?: MaintenanceStatus
  overdue_only?: boolean
}) => {
  return useQuery({
    queryKey: ['maintenance-schedules', filters],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '500' })
      if (filters?.vehicle_id) params.append('vehicle_id', String(filters.vehicle_id))
      if (filters?.status_filter) params.append('status_filter', filters.status_filter)
      if (filters?.overdue_only) params.append('overdue_only', 'true')
      const res = await axiosInstance.get<MaintenanceSchedule[]>(
        `${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/?${params.toString()}`
      )
      return Array.isArray(res.data) ? res.data : []
    },
  })
}

export const useMaintenanceSchedule = (id: number) => {
  return useQuery({
    queryKey: ['maintenance-schedule', id],
    queryFn: async () => {
      const res = await axiosInstance.get<MaintenanceSchedule>(
        `${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/${id}`
      )
      return res.data
    },
    enabled: !!id,
  })
}

export const useMaintenanceAlerts = (daysAhead = 30) => {
  return useQuery({
    queryKey: ['maintenance-alerts', daysAhead],
    queryFn: async () => {
      const res = await axiosInstance.get<MaintenanceAlert[]>(
        `${API_ENDPOINTS.MAINTENANCE_ALERTS}?days_ahead=${daysAhead}`
      )
      return Array.isArray(res.data) ? res.data : []
    },
  })
}

export const useMaintenanceStats = () => {
  return useQuery({
    queryKey: ['maintenance-stats'],
    queryFn: async () => {
      const res = await axiosInstance.get<{
        total_schedules: number
        overdue: number
        upcoming_30_days: number
      }>(API_ENDPOINTS.MAINTENANCE_STATS)
      return res.data
    },
  })
}

export const useVehicleMaintenanceSchedules = (vehicleId: number) => {
  return useQuery({
    queryKey: ['maintenance-schedules', 'vehicle', vehicleId],
    queryFn: async () => {
      const res = await axiosInstance.get<MaintenanceSchedule[]>(
        API_ENDPOINTS.MAINTENANCE_VEHICLE(vehicleId)
      )
      return Array.isArray(res.data) ? res.data : []
    },
    enabled: !!vehicleId,
  })
}

// ─── MUTATIONS ────────────────────────────────────────────────────────────────

export const useCreateMaintenanceSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: MaintenanceScheduleCreate) => {
      const res = await axiosInstance.post<MaintenanceSchedule>(
        `${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/`, data
      )
      return res.data
    },
    onSuccess: () => {
      message.success('Pianificazione creata')
      qc.invalidateQueries({ queryKey: ['maintenance-schedules'] })
      qc.invalidateQueries({ queryKey: ['maintenance-stats'] })
    },
    onError: () => message.error('Errore nella creazione'),
  })
}

export const useUpdateMaintenanceSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<MaintenanceScheduleCreate> }) => {
      const res = await axiosInstance.put<MaintenanceSchedule>(
        `${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/${id}`, data
      )
      return res.data
    },
    onSuccess: () => {
      message.success('Pianificazione aggiornata')
      qc.invalidateQueries({ queryKey: ['maintenance-schedules'] })
    },
    onError: () => message.error('Errore nell\'aggiornamento'),
  })
}

export const useDeleteMaintenanceSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/${id}`)
    },
    onSuccess: () => {
      message.success('Pianificazione eliminata')
      qc.invalidateQueries({ queryKey: ['maintenance-schedules'] })
      qc.invalidateQueries({ queryKey: ['maintenance-stats'] })
    },
    onError: () => message.error('Errore nell\'eliminazione'),
  })
}

export const useCompleteMaintenanceSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: MaintenanceCompleteRequest }) => {
      const res = await axiosInstance.patch<MaintenanceSchedule>(
        `${API_ENDPOINTS.MAINTENANCE_COMPLETE(id)}`, data
      )
      return res.data
    },
    onSuccess: () => {
      message.success('Manutenzione completata')
      qc.invalidateQueries({ queryKey: ['maintenance-schedules'] })
      qc.invalidateQueries({ queryKey: ['maintenance-alerts'] })
      qc.invalidateQueries({ queryKey: ['maintenance-stats'] })
    },
    onError: () => message.error('Errore nel completamento'),
  })
}

export const useSendMaintenanceReminder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: MaintenanceSendReminderRequest }) => {
      const res = await axiosInstance.post<{
        success: boolean
        notification_id: number
        destinatario: string
      }>(`${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/${id}/send-reminder`, data)
      return res.data
    },
    onSuccess: (data) => {
      message.success(`Reminder inviato a ${data.destinatario}`)
      qc.invalidateQueries({ queryKey: ['maintenance-schedules'] })
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || 'Errore nell\'invio del reminder'
      message.error(detail)
    },
  })
}

export const useCreateWorkOrderFromMaintenance = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (scheduleId: number) => {
      const res = await axiosInstance.post<{ work_order_id: number; work_order_numero: string }>(
        `${API_ENDPOINTS.MAINTENANCE_SCHEDULES}/${scheduleId}/create-work-order`
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maintenance-schedules'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || 'Errore nella creazione dell\'accettazione'
      message.error(detail)
    },
  })
}
